import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import SupportThread from '@/components/SupportThread';
import { isUnreadReply, loadSupportMessages, postSupportMessage } from '@/lib/supportTickets';

interface SupportReplyBoxProps {
  ticket: {
    id: string;
    last_support_message_at?: string | null;
    user_last_read_at?: string | null;
  };
}

/**
 * Admin Panel → Support: the conversation on a ticket, and the box to answer.
 * The person reads it under Contact Support → My requests; they get an email
 * that support wrote only if they switched that on.
 */
export default function SupportReplyBox({ ticket }: SupportReplyBoxProps) {
  const queryClient = useQueryClient();
  const { data: messages = [] } = useQuery({
    queryKey: ['sa-ticket-messages', ticket.id],
    queryFn: () => loadSupportMessages([ticket.id]),
    refetchInterval: 30_000,
  });

  const send = async (text: string) => {
    try {
      const { saved, emailed } = await postSupportMessage(ticket.id, text);
      if (!saved) {
        toast.info('Not sent', { description: 'Nothing is sent while viewing as someone else.' });
        return false;
      }
      toast.success('Reply sent', {
        description: emailed
          ? 'They also got an email that support replied.'
          : 'They will see it in Contact Support. (No email: they have not turned support emails on.)',
      });
      queryClient.invalidateQueries({ queryKey: ['sa-ticket-messages', ticket.id] });
      queryClient.invalidateQueries({ queryKey: ['sa-support-tickets'] });
      return true;
    } catch (e) {
      toast.error('Reply not sent', { description: e instanceof Error ? e.message : 'Try again.' });
      return false;
    }
  };

  const answered = !!ticket.last_support_message_at;
  return (
    <div className="space-y-2" data-testid="support-reply-box">
      {answered && (
        <p className="text-[11px] text-muted-foreground">
          {isUnreadReply({ last_support_message_at: ticket.last_support_message_at ?? null, user_last_read_at: ticket.user_last_read_at ?? null })
            ? 'Your last reply: not opened yet'
            : 'Your last reply: seen'}
        </p>
      )}
      <SupportThread
        viewer="support"
        messages={messages}
        onSend={send}
        placeholder="Write a reply. They see it in Contact Support → My requests."
      />
    </div>
  );
}
